import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import commentService from "../../services/commentService";
import { useLanguage } from "../../contexts/LanguageContext.jsx";

/**
 * Composant pour afficher et gérer les commentaires d'une recette
 * Permet d'ajouter, modifier, supprimer et liker des commentaires
 */
const RecipeComments = ({ recipeId, user, allowComments = true }) => {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [newComment, setNewComment] = useState("");
  const [newRating, setNewRating] = useState(0);
  const [editingCommentId, setEditingCommentId] = useState(null);
  const [editText, setEditText] = useState("");
  const [editRating, setEditRating] = useState(0);

  useEffect(() => {
    loadComments();
  }, [recipeId]);

  const loadComments = async () => {
    try {
      const data = await commentService.getRecipeComments(recipeId, 100);
      setComments(data.comments || []);
    } catch (error) {
      console.error("Erreur lors du chargement des commentaires:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitComment = async (e) => {
    e.preventDefault();

    if (!user) {
      navigate("/auth");
      return;
    }

    if (!newComment.trim()) return;

    setSubmitting(true);
    try {
      await commentService.createComment(
        recipeId,
        newComment.trim(),
        newRating > 0 ? newRating : null,
      );

      setNewComment("");
      setNewRating(0);
      await loadComments();
    } catch (error) {
      console.error("Erreur lors de l'ajout du commentaire:", error);
      alert(t("errorAddingComment"));
    } finally {
      setSubmitting(false);
    }
  };

  const handleEditComment = async (commentId) => {
    if (!editText.trim()) return;

    setSubmitting(true);
    try {
      await commentService.updateComment(
        commentId,
        editText.trim(),
        editRating > 0 ? editRating : null,
      );

      setEditingCommentId(null);
      setEditText("");
      setEditRating(0);
      await loadComments();
    } catch (error) {
      console.error("Erreur lors de la modification:", error);
      alert(t("errorEditingComment"));
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteComment = async (commentId) => {
    if (!window.confirm(t("confirmDeleteComment"))) {
      return;
    }

    try {
      await commentService.deleteComment(commentId);
      await loadComments();
    } catch (error) {
      console.error("Erreur lors de la suppression:", error);
      alert(t("errorDeletingComment"));
    }
  };

  const handleLikeComment = async (commentId) => {
    if (!user) {
      navigate("/auth");
      return;
    }

    try {
      await commentService.likeComment(commentId);
      await loadComments();
    } catch (error) {
      console.error("Erreur lors du like:", error);
    }
  };

  const startEditing = (comment) => {
    setEditingCommentId(comment.id);
    setEditText(comment.comment_text);
    setEditRating(comment.rating || 0);
  };

  const cancelEditing = () => {
    setEditingCommentId(null);
    setEditText("");
    setEditRating(0);
  };

  const renderStars = (rating, interactive = false, onRate = null) => {
    return (
      <div className="flex items-center space-x-1">
        {[1, 2, 3, 4, 5].map((star) => (
          <button
            key={star}
            type="button"
            onClick={() => interactive && onRate && onRate(star)}
            className={`text-2xl ${
              interactive
                ? "cursor-pointer hover:scale-110 transition-transform"
                : ""
            } ${star <= rating ? "text-yellow-400" : "text-gray-300"}`}
            disabled={!interactive}
          >
            ★
          </button>
        ))}
      </div>
    );
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffTime = Math.abs(now - date);
    const diffMinutes = Math.ceil(diffTime / (1000 * 60));

    if (diffMinutes < 60) return `Il y a ${diffMinutes} min`;
    const diffHours = Math.ceil(diffMinutes / 60);
    if (diffHours < 24) return `Il y a ${diffHours}h`;
    const diffDays = Math.ceil(diffHours / 24);
    if (diffDays < 7) return `Il y a ${diffDays}j`;
    return date.toLocaleDateString("fr-FR");
  };

  const getInitials = (comment) => {
    if (comment.first_name && comment.last_name) {
      return `${comment.first_name[0]}${comment.last_name[0]}`.toUpperCase();
    }
    return comment.username?.[0]?.toUpperCase() || "U";
  };

  if (!allowComments) {
    return (
      <div className="bg-gray-50 rounded-2xl p-6 text-center">
        <p className="text-gray-600">
          Les commentaires sont désactivés pour cette recette
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* En-tête */}
      <div className="flex items-center justify-between">
        <h3 className="text-2xl font-bold text-gray-900 flex items-center space-x-2">
          <span>💬</span>
          <span>Commentaires</span>
          <span className="text-lg text-gray-500">({comments.length})</span>
        </h3>
      </div>

      {/* Formulaire d'ajout de commentaire */}
      {user && (
        <form
          onSubmit={handleSubmitComment}
          className="bg-white rounded-2xl shadow-sm p-6 border border-gray-100"
        >
          <div className="flex items-start space-x-4">
            <div className="w-12 h-12 bg-gradient-to-br from-indigo-600 to-purple-600 rounded-xl flex items-center justify-center text-white font-bold flex-shrink-0">
              {user.first_name?.[0] || user.username?.[0] || "U"}
            </div>

            <div className="flex-1">
              <textarea
                value={newComment}
                onChange={(e) => setNewComment(e.target.value)}
                placeholder="Partagez votre avis sur cette recette..."
                className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent resize-none"
                rows="3"
              />

              <div className="mt-4 flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600 mb-2 font-medium">
                    Votre note (optionnel)
                  </p>
                  {renderStars(newRating, true, setNewRating)}
                </div>

                <button
                  type="submit"
                  disabled={submitting || !newComment.trim()}
                  className="px-6 py-3 bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-xl font-medium hover:from-indigo-700 hover:to-purple-700 shadow-md transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {submitting ? "Envoi..." : "Publier"}
                </button>
              </div>
            </div>
          </div>
        </form>
      )}

      {/* Message si non connecté */}
      {!user && (
        <div className="bg-indigo-50 rounded-2xl p-6 text-center">
          <p className="text-gray-700 mb-4">
            Connectez-vous pour laisser un commentaire
          </p>
          <button
            onClick={() => navigate("/auth")}
            className="px-6 py-3 bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-xl font-medium hover:from-indigo-700 hover:to-purple-700 shadow-md transition-all duration-200"
          >
            Se connecter
          </button>
        </div>
      )}

      {/* Liste des commentaires */}
      {loading ? (
        <div className="text-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Chargement des commentaires...</p>
        </div>
      ) : comments.length > 0 ? (
        <div className="space-y-4">
          {comments.map((comment) => (
            <div
              key={comment.id}
              className="bg-white rounded-2xl shadow-sm p-6 border border-gray-100"
            >
              {editingCommentId === comment.id ? (
                // Mode édition
                <div className="space-y-4">
                  <textarea
                    value={editText}
                    onChange={(e) => setEditText(e.target.value)}
                    className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent resize-none"
                    rows="3"
                  />

                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-600 mb-2">Votre note</p>
                      {renderStars(editRating, true, setEditRating)}
                    </div>

                    <div className="flex items-center space-x-2">
                      <button
                        onClick={cancelEditing}
                        className="px-4 py-2 bg-gray-100 text-gray-700 rounded-xl font-medium hover:bg-gray-200 transition-colors"
                      >
                        Annuler
                      </button>
                      <button
                        onClick={() => handleEditComment(comment.id)}
                        disabled={submitting || !editText.trim()}
                        className="px-4 py-2 bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-xl font-medium hover:from-indigo-700 hover:to-purple-700 transition-all duration-200 disabled:opacity-50"
                      >
                        {submitting ? "..." : "Enregistrer"}
                      </button>
                    </div>
                  </div>
                </div>
              ) : (
                // Mode affichage
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-gradient-to-br from-indigo-600 to-purple-600 rounded-xl flex items-center justify-center text-white font-bold flex-shrink-0">
                    {comment.avatar_url ? (
                      <img
                        src={comment.avatar_url}
                        alt={comment.username}
                        className="w-full h-full rounded-xl object-cover"
                      />
                    ) : (
                      getInitials(comment)
                    )}
                  </div>

                  <div className="flex-1">
                    <div className="flex items-start justify-between mb-2">
                      <div>
                        <h4 className="font-semibold text-gray-900">
                          {comment.first_name && comment.last_name
                            ? `${comment.first_name} ${comment.last_name}`
                            : comment.username}
                        </h4>
                        <p className="text-sm text-gray-500">
                          {formatDate(comment.created_at)}
                        </p>
                      </div>

                      {comment.rating && (
                        <div>{renderStars(comment.rating)}</div>
                      )}
                    </div>

                    <p className="text-gray-700 mb-3 whitespace-pre-wrap">
                      {comment.comment_text}
                    </p>

                    <div className="flex items-center space-x-4">
                      <button
                        onClick={() => handleLikeComment(comment.id)}
                        className="flex items-center space-x-1 text-sm text-gray-500 hover:text-red-500 transition-colors"
                      >
                        <span>❤️</span>
                        <span>{comment.likes_count || 0}</span>
                      </button>

                      {user && user.id === comment.user_id && (
                        <>
                          <button
                            onClick={() => startEditing(comment)}
                            className="text-sm text-indigo-600 hover:text-indigo-700 font-medium"
                          >
                            Modifier
                          </button>
                          <button
                            onClick={() => handleDeleteComment(comment.id)}
                            className="text-sm text-red-600 hover:text-red-700 font-medium"
                          >
                            Supprimer
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      ) : (
        <div className="text-center py-12 bg-white rounded-2xl shadow-sm">
          <div className="text-6xl mb-4">💬</div>
          <h3 className="text-xl font-bold text-gray-900 mb-2">
            Aucun commentaire
          </h3>
          <p className="text-gray-600">
            Soyez le premier à partager votre avis sur cette recette !
          </p>
        </div>
      )}
    </div>
  );
};

export default RecipeComments;
